// NOTE: NSAI-5411 — a path segment the URL parser treats as "this directory"
// or "the parent directory". The WHATWG parser (browsers) and the native
// parsers mobile sits on also accept the percent-encoded spellings, so `%2e`
// counts as a dot here too.
const DOT_PATH_SEGMENT_PATTERN = /^(?:\.|%2e){1,2}$/i;

// NOTE: Browsers treat a backslash as a path separator in http(s) URLs, so it
// splits segments exactly like a slash does.
//
// NOTE: `%2f` / `%5c` and double-encoded `%252e` are out of scope on purpose:
// the WHATWG URL parser treats none of them as a separator or a dot segment,
// so they cannot redirect the client's request — decoding them is the server's job.
const PATH_SEPARATOR_PATTERN = /[/\\]/;

const QUERY_OR_FRAGMENT_START_PATTERN = /[?#]/;

export const isDotPathSegment = (segment: string): boolean =>
  DOT_PATH_SEGMENT_PATTERN.test(segment);

// NOTE: Every identifier interpolated into a REST path goes through this. A raw
// identifier could otherwise carry a slash or a query character and address a
// different route than the caller intended — and when the identifier comes
// from a route param (a share token, a chat id in the address bar), the value
// is attacker-chosen: `../../chat-folders/<uuid>/delete?x#` would turn the
// app's own authenticated request into a call to any other endpoint.
//
// NOTE: `encodeURIComponent` leaves dots alone, so a segment that is exactly
// `.` or `..` would still step out of the intended route after encoding. It
// is never a valid identifier, so it is refused rather than sent.
export const encodePathSegment = (segment: string): string => {
  const encoded = encodeURIComponent(segment);

  if (isDotPathSegment(encoded)) {
    throw new Error("Refusing to build an API path from a dot segment");
  }

  return encoded;
};

// NOTE: True when the path part of `url` (everything before `?` or `#`) holds a
// `.` or `..` segment. No API route this app calls has one, so its presence
// means an identifier escaped encoding and is about to redirect the request.
export const hasDotPathSegment = (url: string): boolean => {
  const pathEnd = url.search(QUERY_OR_FRAGMENT_START_PATTERN);
  const path = pathEnd === -1 ? url : url.slice(0, pathEnd);

  return path.split(PATH_SEPARATOR_PATTERN).some(isDotPathSegment);
};

// NOTE: The HTTP clients' last line of defence (NSAI-5411): refuse to put a
// request on the wire whose path would be normalized onto a different route.
// It backs up `encodePathSegment` for every call site that still interpolates
// an identifier raw.
export const assertNoDotPathSegment = (url: string | undefined): void => {
  if (url && hasDotPathSegment(url)) {
    throw new Error("Refusing to send an API request with a dot path segment");
  }
};
